import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  createEditor,
  Descendant,
  Editor,
  Transforms,
  Element as SlateElement,
} from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { withHistory } from "slate-history";
import "./LegalEditor.css";
import { JSX } from "react/jsx-runtime";
import SlateDebugView from "../../../components/SlateDebugView";
import { BlockType, createParagraphNode } from "../../../utils/nodeUtils";
import { Leaf } from "../../../components/Slate/Leaf";
import HoveringToolbar from "./HoveringToolbar/HoveringToolbar";
import { CustomText } from "../../../components/Slate/CustomText";
import { BaseNode, CustomNode } from "../../../components/Slate/BaseNode";
import { useDocument } from "../../../contexts/DocumentContext";
import CommentListItem from "../../../components/CommentListItem/CommentListItem";
import Toolbar from "./Toolbar";

interface LegalEditorProps {
  documentId: string;
  initialValue?: Descendant[];
  onChange?: (value: Descendant[]) => void;
}

const LegalEditor: React.FC<LegalEditorProps> = ({
  initialValue = [createParagraphNode()],
  onChange,
  documentId,
}) => {
  const { comments } = useDocument();
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const [value, setValue] = useState<Descendant[]>(initialValue);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [lineCount, setLineCount] = useState(1);
  const [showDebugView, setShowDebugView] = useState(false);
  const [selectedNodeRef, setSelectedNodeRef] = useState<HTMLElement | null>(
    null,
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodePositions, setNodePositions] = useState<
    Record<string, { top: number }>
  >({});

  useEffect(() => {
    const updatePositions = () => {
      const nodes = Array.from(
        Editor.nodes(editor, {
          at: [], // Search entire document
          match: (n) =>
            !Editor.isEditor(n) && (n as BaseNode).type === "paragraph",
        }),
      );

      const anchorCommentIds = comments.map((comment) => comment.anchorNodeId);

      const nodeIdsThatmatter = nodes.filter(([node, path]) => {
        if (!(node as BaseNode).id) return false;
        return anchorCommentIds.includes((node as BaseNode).id);
      });

      const newPositions: Record<string, { top: number }> = {};
      for (const [node] of nodeIdsThatmatter) {
        if (!(node as BaseNode).id) continue; // Ensure node has an ID

        // Find the corresponding DOM element using the node ID
        const nodeElement = document.querySelector(
          `[data-node-id="${(node as BaseNode).id}"]`,
        );
        if (!nodeElement) continue;

        // Get position
        const rect = nodeElement.getBoundingClientRect();
        newPositions[(node as BaseNode).id] = {
          top: rect.top + window.scrollY,
        };
      }

      setNodePositions(newPositions);
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    window.addEventListener("scroll", updatePositions);
    return () => {
      window.removeEventListener("resize", updatePositions);
      window.removeEventListener("scroll", updatePositions);
    };
  }, [comments]);

  const renderLeaf = useCallback(
    (
      props: JSX.IntrinsicAttributes & {
        attributes: any;
        children: any;
        leaf: any;
      },
    ) => <Leaf {...props} />,
    [],
  );
  const renderElement = useCallback(
    ({
      attributes,
      children,
      element,
    }: {
      attributes: any;
      children: React.ReactNode;
      element: any;
    }) => {
      switch (element.type) {
        case "heading-one":
          return <h1 {...attributes}>{children}</h1>;
        case "heading-two":
          return <h2 {...attributes}>{children}</h2>;
        default:
          return (
            <p data-node-id={(element as CustomNode).id} {...attributes}>
              {children}
            </p>
          );
      }
    },
    [],
  );

  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value, onChange]);

  useEffect(() => {
    const updateLineCount = () => {
      if (editorRef.current) {
        const lineHeight = parseInt(
          getComputedStyle(editorRef.current).lineHeight || "20",
          10,
        );
        setLineCount(Math.ceil(editorRef.current.scrollHeight / lineHeight));
      }
    };
    updateLineCount();
    window.addEventListener("resize", updateLineCount);
    return () => window.removeEventListener("resize", updateLineCount);
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default Enter behavior
      const newNode = createParagraphNode();
      Transforms.insertNodes(editor, newNode); // Insert the new node
    }
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "b":
          event.preventDefault();
          toggleMark(editor, "bold");
          break;
        case "i":
          event.preventDefault();
          toggleMark(editor, "italic");
          break;
        case "u":
          event.preventDefault();
          toggleMark(editor, "underline");
          break;
        case "1":
          event.preventDefault();
          toggleBlock(editor, "heading-one");
          break;
        case "2":
          event.preventDefault();
          toggleBlock(editor, "heading-two");
          break;
        case "3":
          event.preventDefault();
          toggleBlock(editor, "heading-three");
          break;
      }
    }
  };

  const handleSelect = (e: any) => {
    const { selection } = editor;

    if (!selection || !Editor.string(editor, selection).trim()) {
      setSelectedNodeRef(null);
      return;
    }

    const match = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && n.type === "paragraph", // Ensure it's a paragraph node
      mode: "lowest",
    }).next().value;

    if (match) {
      const [node] = match as [CustomNode, any];
      setSelectedNodeId(node.id);
    } else {
      setSelectedNodeId(null);
    }

    const domSelection = window.getSelection();
    if (!domSelection) {
      return;
    }

    const domRange = domSelection.getRangeAt(0);
    const selectedNode = domRange.startContainer.parentElement;
    setSelectedNodeRef(selectedNode as HTMLElement);
  };

  return (
    <div className="flex w-full h-full">
      <div className="flex-grow">
        <Slate editor={editor} initialValue={value} onChange={setValue}>
          <div className="flex items-center justify-between">
            <Toolbar />
            {/* <Switch
              checked={showDebugView}
              onChange={() => setShowDebugView(!showDebugView)}
            /> */}
          </div>
          {showDebugView && <SlateDebugView initialValue={value} />}
          <div className="flex border border-gray-300 rounded-md editor-container">
            <div className="line-numbers">
              {Array.from({ length: lineCount }, (_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>
            <div className="flex-grow editor-content" ref={editorRef}>
              <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                onKeyDown={handleKeyDown}
                onSelect={handleSelect}
              />
            </div>
          </div>
        </Slate>
      </div>

      {/* Red View (Always on Right) */}
      <div className="flex flex-col w-64 h-full p-4">
        {Object.entries(nodePositions).map(([nodeId, { top }]) => {
          const comment = comments.find((c) => c.anchorNodeId === nodeId);
          if (!comment) return null;
          return (
            <CommentListItem
              key={comment._id}
              style={{ top, position: "absolute" }}
              className={"w-64"}
              comment={comment}
            />
          );
        })}
        <HoveringToolbar
          documentId={documentId}
          selectedNodeId={selectedNodeId}
          selectedNodeRef={selectedNodeRef}
        />
      </div>
    </div>
  );
};

// **Toggle Text Formatting (Bold, Italic, Underline)**
const toggleMark = (editor: Editor, format: keyof Omit<CustomText, "text">) => {
  const isActive = Editor.marks(editor)?.[format] === true;
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// **Toggle Block (H1, H2)**
const toggleBlock = (editor: Editor, format: BlockType) => {
  const isActive = isFormatActive(editor, format);
  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : format,
  } as Partial<SlateElement>);
};

// **Check if Format is Active**
const isFormatActive = (
  editor: Editor,
  format: BlockType | keyof Omit<CustomText, "text">,
) => {
  if (["bold", "italic", "underline"].includes(format)) {
    return (
      Editor.marks(editor)?.[format as keyof Omit<CustomText, "text">] === true
    );
  }

  const match = Editor.nodes(editor, {
    match: (n) => (n as any).type === format,
  }).next().value;

  return !!match;
};

export default LegalEditor;
